<template>
  <f7-sheet
    :opened="opened"
    class="supplier-suggestion-sheet"
    backdrop
    @sheet:closed="onClosed"
    @update:opened="(value) => emit('update:opened', value)"
  >
    <f7-toolbar>
      <div class="left">
        <f7-link sheet-close>{{ t('general.close') }}</f7-link>
      </div>
      <div class="title">{{ t(titleKey, { item: tipTargetName, variant: tipTargetVariant }) }}</div>
      <div class="right" />
    </f7-toolbar>

    <f7-page-content>
      <f7-block class="supplier-suggestion-sheet__intro">
        <p>{{ t(introKey) }}</p>
        <!-- WHAT NAMING A SHOP EARNS (BE→FE brief 2026-10-02-suggestion-reward).
             The amount is `meta.rewardCents` off the roster read — never typed
             here — and the line is simply absent while the reward is off. Not in
             answer mode: a shop named inside a live question rides her answer,
             and that path records no suggestion to reward. -->
        <p v-if="rewardAmount" class="supplier-suggestion-sheet__reward">
          {{ t('item.suggestSupplier.rewardLine', { amount: rewardAmount }) }}
        </p>
      </f7-block>

      <!-- THE ROSTER IS THE SHEET (oos-hold P3-A). Three states, and only one
           of them can submit.

           Both doors take `supplier_id` REQUIRED now and 422 on a typed name
           (brief 2026-09-15-roster-only-tips-once-per-line, owner answer H-C),
           so the list is no longer a convenience sitting above a free-text
           escape hatch — it IS the door. That removed four controls from this
           sheet: the intent pair, the «مش موجود في القائمة» row, and the typed
           field. What is left is one choice and two optional facts. -->

      <!-- 1. STILL ASKING. A preloader, not an empty list: an empty list and a
              failed one are different sentences and must not share a frame. -->
      <f7-block v-if="rosterLoading" class="supplier-suggestion-sheet__loading">
        <f7-preloader size="28"></f7-preloader>
      </f7-block>

      <!-- 2. NO ROSTER. It failed to load, or the club has no shops on it.
              Either way the door cannot open, so the sheet SAYS SO and offers
              nothing — the submit below is withheld too. This is the change the
              roster-only rule forces: a silent empty list used to be fine
              because the typed field was the real path, and now there is
              nothing behind it but a guaranteed 422. -->
      <f7-block v-else-if="!hasSupplierList" class="supplier-suggestion-sheet__empty">
        <p role="alert">{{ t('item.suggestSupplier.rosterUnavailable') }}</p>
      </f7-block>

      <!-- 3. THE LIST. -->
      <template v-else>
        <f7-block-title>{{ t('item.suggestSupplier.pickLabel') }}</f7-block-title>

        <!-- Only past the point where scanning stops being easier than typing.
             A club with eight shops does not need a search box above eight
             rows; one with forty does. The threshold is the cut — a control
             that helps nobody on the common roster is a control that ships to
             everybody. -->
        <f7-searchbar
          v-if="showSearch"
          v-model:value="search"
          custom-search
          disable-button
          clear-button
          :placeholder="t('item.suggestSupplier.searchPlaceholder')"
          :disable-button-text="t('general.cancel')"
          @searchbar:clear="search = ''"
          @searchbar:disable="search = ''"
        ></f7-searchbar>

        <f7-list
          strong-ios
          outline-ios
          dividers-ios
          class="supplier-suggestion-sheet__list supplier-suggestion-sheet__list--picker"
        >
          <f7-list-item
            v-for="supplier in filteredSuppliers"
            :key="supplier.id"
            radio
            :checked="String(pickedId) === String(supplier.id)"
            name="supplier-suggestion-pick"
            :title="supplier.name"
            :disabled="submitting"
            @change="pick(supplier.id)"
          >
            <!-- Only a shop other members already named for THIS product. The
                 count is of OTHER members, and never says who (owner G-J). -->
            <template v-if="supplier.namedByMembers > 0" #after>
              <span class="supplier-suggestion-sheet__named">
                {{ t('item.suggestSupplier.namedTag', { count: supplier.namedByMembers }) }}
              </span>
            </template>
          </f7-list-item>
        </f7-list>

        <!-- THE PRICE SHE SAW. Optional, and the only NEW field here — it
             replaces the "any price is fine" checkbox with the fact that
             checkbox was groping at, and the leader's accept sheet defaults its
             price from it (owner answer H-H). Whole pounds, because that is how
             a price is read off a shelf; the piasters conversion is this
             component's job, not hers.

             Hidden in answer mode: a shop named INSIDE a live question rides
             `respond`, which has nowhere to put a price. Offering a field that
             is silently dropped would be the sheet lying about what it sends. -->
        <f7-block v-if="!isAnswerMode" class="supplier-suggestion-sheet__field">
          <label class="supplier-suggestion-sheet__label" :for="priceFieldId">
            {{ t('item.suggestSupplier.priceSeenLabel') }}
          </label>
          <input
            :id="priceFieldId"
            v-model="priceSeen"
            type="number"
            inputmode="decimal"
            :min="PRICE_SEEN_MIN_POUNDS"
            step="1"
            class="supplier-suggestion-sheet__input"
            :placeholder="t('item.suggestSupplier.priceSeenPlaceholder')"
            :disabled="submitting"
            dir="auto"
          />
          <p class="supplier-suggestion-sheet__hint">
            {{ t('item.suggestSupplier.priceSeenHint') }}
          </p>
        </f7-block>

        <!-- THE OPTIONAL SENTENCE. Same shape as OptionalNote's textarea on the
             answer path — a plain field, never an overlay — but without its
             photo half: nothing here is evidence, it is directions to a shop. -->
        <f7-block class="supplier-suggestion-sheet__field">
          <label class="supplier-suggestion-sheet__label" :for="noteFieldId">
            {{ t('item.suggestSupplier.noteLabel') }}
          </label>
          <textarea
            :id="noteFieldId"
            v-model="note"
            class="supplier-suggestion-sheet__textarea"
            :placeholder="t('item.suggestSupplier.notePlaceholder')"
            :maxlength="SUPPLIER_SUGGESTION_NOTE_MAX"
            :disabled="submitting"
            rows="2"
            dir="auto"
          />
          <p class="supplier-suggestion-sheet__hint">{{ t('item.suggestSupplier.noteHint') }}</p>
        </f7-block>

        <!-- A SHOP ALREADY NAMED IS STILL PICKABLE (owner G-D). Nothing here
             disables the submit or asks her to choose differently: her naming
             counts as a vote for where the team goes first, and only the reward
             belongs to whoever named it first. -->
        <f7-block v-if="pickedShopAlreadyNamed" class="supplier-suggestion-sheet__vote">
          <p>{{ t('item.suggestSupplier.namedVoteLine') }}</p>
        </f7-block>
      </template>

      <!-- One message region for both the client-side "you named no shop" and
           every server refusal, so two failures can never stack on screen. -->
      <f7-block v-if="errorMessage" class="supplier-suggestion-sheet__error">
        <p role="alert">{{ errorMessage }}</p>
      </f7-block>
    </f7-page-content>

    <!--
      The submit lives OUTSIDE the scroller. F7-Vue renders the #fixed slot as a
      direct child of .sheet-modal — `[fixedList, slots.fixed(), <div
      class="sheet-modal-inner">]` (framework7-vue/components/sheet.js:206-212) —
      so this is a SIBLING of the scroll container and reaching it does not
      depend on scrolling at all. That is the whole fix: on the owner's iPhone
      inside Telegram the body would not scroll by touch and «إرسال» simply could
      not be reached. The shared `sheet-fixed-footer` rules, including the
      load-bearing z-index, live in src/css/app.css.
    -->
    <template #fixed>
      <!-- WITHHELD WITHOUT A ROSTER, not merely disabled. With `supplier_id`
           required there is no body this sheet could build from an empty list,
           so a send button there could only ever 422 — and a greyed-out button
           beside a message she cannot act on reads as something she did wrong.
           The sentence above is the whole screen in that state. -->
      <div v-if="hasSupplierList" class="sheet-fixed-footer">
        <f7-button
          fill
          large
          :disabled="submitting"
          @click="submit"
        >
          <f7-preloader
            v-if="submitting"
            size="20"
            class="margin-end-half"
          ></f7-preloader>
          {{ t('item.suggestSupplier.submit') }}
        </f7-button>
      </div>
    </template>
  </f7-sheet>
</template>

<script setup>
/**
 * SupplierSuggestionSheet — the member's door onto BOTH suggestion endpoints.
 *
 * BE→FE briefs: `2026-09-05-member-supplier-suggestion.md` (the line door),
 * `2026-09-09-suggestion-carries-its-answer.md` (intent + any_price),
 * `2026-09-09-product-level-suggestion-and-oos-feed.md` (the product door).
 *
 * She names a shop for something nobody could source — "Trust has it, second
 * floor" — either by picking one the club already deals with or by typing a
 * name, plus one optional sentence. The admin then accepts (naming a day) or
 * declines, and either way it pushes back to her.
 *
 * ── THREE MODES, ONE SHEET ──────────────────────────────────────────────────
 * `line` (the default) posts to `POST /order-box-items/{id}/suggest-supplier` —
 * her own order line, the door that has existed since September 5th.
 *
 * `product` posts to `POST /products/{id}/suggest-supplier`, which exists for
 * the one person the first door refused: the member reading «مش متوفر» on a post
 * BEFORE she orders, who is exactly who that badge is for.
 *
 * `answer` posts NEITHER. It is the fourth reply on a LIVE question, and since
 * 2026-09-15 a shop named there rides her ANSWER —
 * `POST /item-issues/{id}/-actions/respond` with `response: 'wait'` and
 * `payload: { supplier_id, note }` (brief `2026-09-15-the-shop-rides-the-answer.md`,
 * owner answer H-G). The host owns that call, so this mode emits `pick` and the
 * card sends it; the sheet's own two doors are not involved. The backend still
 * has a transitional branch that would accept the tip door on such a line and
 * quietly record an answer instead, and nothing here uses it any more — that is
 * the point of this mode.
 *
 * The differences are not cosmetic and not optional. `intent` was always a 422
 * on the product door; `price_seen_cents` has nowhere to go on the answer path
 * and the price field is hidden there rather than silently dropped.
 *
 * ── THE ROSTER IS THE DOOR (2026-09-15, owner answers H-B / H-C) ─────────────
 * Both endpoints now require `supplier_id` from `GET /api/v1/suppliers` and
 * answer 422 to `supplier_name`, `intent` or `any_price`. So the sheet lost
 * four controls at once — the intent question, the «مش موجود في القائمة» row,
 * the typed-name field and the "any price is fine" checkbox — and gained one
 * optional number, the price she saw.
 *
 * That makes the roster LOAD-BEARING where it used to be a convenience. It is
 * membership-gated and can fail, and there is no longer a typed field behind
 * it, so a failed or empty list is now a stated, submit-less state rather than
 * a silent one. `loading` and `failed` come from the host's `useSupplierList`
 * precisely so those two can be told apart on screen.
 *
 * The line door is also ONCE PER LINE for the line's whole life. That gate is
 * NOT here — it is `canSuggestSupplier()` reading `supplierSuggestionUsed`,
 * which is what stops the door being offered at all. This sheet only ever opens
 * on a line that may still use it.
 *
 * ── One submit, three refusals ───────────────────────────────────────────────
 * 403 means the line moved past suggesting (collected, packed, shipped) — the
 * door itself is stale, so this sheet emits `stale` and gets out of the way: the
 * host closes it, speaks the message as a toast and re-reads the line, which is
 * what makes the door disappear rather than merely refuse. 409 means the
 * collection run closed under her while she typed, and 422 is a validation
 * refusal carrying the backend's own sentence (preferred over our copy exactly
 * as the cancel path prefers `error.detail`) — both are about what she typed, so
 * both stay on screen beside the field she can change.
 *
 * ── A second send REPLACES the first ─────────────────────────────────────────
 * The backend UPDATES an open suggestion rather than adding a row, so "correct
 * what I told you" is just re-opening this sheet. It seeds from the item's own
 * open `supplierSuggestion` so she is editing what she sent, not retyping it.
 */
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency';
import { useToast } from '@/composables/useToast';
import OrderBoxRepository from '@/repositories/OrderBoxRepository.js';
import {
  ORDER_BOX_ITEM_SUGGEST_FORBIDDEN,
  ORDER_BOX_ITEM_SUGGEST_ROUND_CLOSED,
} from '@/services/orderBoxItem.service.js';
import {
  PRODUCT_SUGGEST_FORBIDDEN,
  suggestSupplierForProduct,
} from '@/services/productSuggestion.service.js';
import { matchesAllWords } from '@/utils/arabicSearch.js';
import { isManualItem, itemDisplayName } from '@/utils/orderBoxItemDisplay.js';
import {
  openSupplierSuggestion,
  PRICE_SEEN_MIN_CENTS,
  SUPPLIER_SUGGESTION_NOTE_MAX,
} from '@/utils/supplierSuggestion.js';

defineOptions({ name: 'SupplierSuggestionSheet' });

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  // The line she is suggesting for. Null while the host has nothing selected —
  // the sheet simply cannot submit in that state.
  item: {
    type: Object,
    default: null,
  },
  // `[{ id, name }]`, from `GET /api/v1/suppliers` via `useSupplierList`. Both
  // hosts fetch it when they open this sheet, so it usually arrives AFTER the
  // seeding watcher has run — which is why that seed is re-applied when this
  // prop changes. Empty is a supported, silent state; see the docblock.
  suppliers: {
    type: Array,
    default: () => [],
  },
  // `line` (her own order line), `product` (something she has merely read about)
  // or `answer` (a shop named inside a live question, which the HOST posts
  // through `respond`). See the docblock.
  mode: {
    type: String,
    default: 'line',
    validator: (value) => ['line', 'product', 'answer'].includes(value),
  },
  // Is the roster still being fetched? Load-bearing now that there is no typed
  // field behind an empty list: without it "still asking" and "there is no
  // list" would render as the same blank sheet.
  loading: {
    type: Boolean,
    default: false,
  },
  // Did the roster READ fail (403, offline, 5xx)? Kept separate from an empty
  // list only so a future sentence can tell them apart; both currently close
  // the door, because neither can produce a `supplier_id`.
  failed: {
    type: Boolean,
    default: false,
  },
  // In `answer` mode: the shop and note already on her answer
  // (`memberSupplierId` / `memberComment` off the issue), so editing an answer
  // re-selects what she named instead of making her pick it again. Unused in
  // the other two modes, which seed from the tip on the item itself.
  seedSupplierId: {
    type: Number,
    default: null,
  },
  seedNote: {
    type: String,
    default: '',
  },
  // `meta.rewardCents` from the roster read, in piasters — what naming a shop
  // earns per helped member. Null while the backend has the reward off.
  rewardCents: {
    type: Number,
    default: null,
  },
  // The product she is suggesting for, in `product` mode. Carries `mySuggestion`
  // — the SAME payload shape `supplierSuggestion` carries on a line — so the
  // seeding below reads one or the other by key and needs no second branch.
  product: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:opened', 'submitted', 'stale', 'pick']);

const { t } = useI18n({ useScope: 'global' });
const { showError } = useToast();
const { formatAmount } = useCurrency();

// Stable, collision-free ids so `label for=` holds if two sheets ever coexist.
const uid = useId();
const priceFieldId = computed(() => `supplier-suggestion-price-${uid}`);
const noteFieldId = computed(() => `supplier-suggestion-note-${uid}`);

/** The floor, in whole pounds, for the field and its refusal. */
const PRICE_SEEN_MIN_POUNDS = PRICE_SEEN_MIN_CENTS / 100;

/** Past this many rows, scanning is slower than typing and the searchbar earns
 *  its place. Below it, a search box above a handful of shop names is one more
 *  control to read past. */
const SEARCH_THRESHOLD = 8;

const ARABIC_INDIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

/**
 * ٠-٩ → 0-9. Some Arabic-locale mobile keyboards write Arabic-Indic digits
 * into this field even though it is `type="number"` — `Number('٤٥')` is
 * `NaN`, so without this her price was silently DROPPED (treated as empty)
 * rather than kept or refused (tips-wave P3-A, U-79/W-C).
 */
function toWesternDigits(value) {
  if (typeof value !== 'string') return value;
  return value.replace(/[٠-٩]/g, (digit) => String(ARABIC_INDIC_DIGITS.indexOf(digit)));
}

const search = ref('');
// null means "she has not picked yet" — and with the typed field gone that is
// now the ONLY thing it can mean. It used to double as "none of the club's
// shops", which is the state that no longer exists.
const pickedId = ref(null);
const note = ref('');
// Whole pounds as she types them, a STRING off the input. Converted to piasters
// once, at submit — never held as cents, so the field can round-trip what she
// actually typed.
const priceSeen = ref('');
const submitting = ref(false);
const errorMessage = ref(null);
// Has she changed anything since this sheet opened? Used for one thing only:
// stopping a late-arriving roster from moving a choice she has already made.
const touched = ref(false);

const hasSupplierList = computed(() => props.suppliers.length > 0);

// Only while the host says so AND nothing has arrived. A list that landed is
// never "still loading" even if a second read is in flight behind it.
const rosterLoading = computed(() => props.loading && !hasSupplierList.value);

const showSearch = computed(() => props.suppliers.length > SEARCH_THRESHOLD);

const isProductMode = computed(() => props.mode === 'product');
const isAnswerMode = computed(() => props.mode === 'answer');

// The reward as the number of PoolShare she would read (1000 piasters → "10"),
// or '' when there is nothing to promise: the reward is off, or this is the
// answer path, which records no suggestion.
const rewardAmount = computed(() =>
  !isAnswerMode.value && props.rewardCents > 0
    ? formatAmount(props.rewardCents, { trimZeros: true })
    : '',
);

// Has another member already named the shop she has picked? Presence-tested off
// the row: a roster read that was not asked about a product carries no count.
const pickedShopAlreadyNamed = computed(() => {
  if (pickedId.value == null) return false;
  const row = props.suppliers.find((supplier) => Number(supplier?.id) === Number(pickedId.value));
  return (row?.namedByMembers ?? 0) > 0;
});

// The thing she is suggesting for, and the attribute her existing tip lives
// under. `mySuggestion` and `supplierSuggestion` carry byte-identical payloads
// from the same backend projection, so one seeding path serves both.
const target = computed(() => (isProductMode.value ? props.product : props.item));

// THE HEADER NAMES WHAT SHE IS TIPPING FOR (tips-wave P3-A, U-79). A bare
// «قول لنا مين عنده الصنف ده» read the same on every line she ever opened this
// sheet from, on a page that already carries dozens of them. Manual (off-
// catalog) lines carry NO real variant — the backend mirrors `manualLabel`
// into `variantName` for backwards compat, so showing both would repeat the
// same word twice.
const tipTargetName = computed(() => {
  if (isProductMode.value) return props.product?.name ?? '';
  return itemDisplayName(props.item);
});
const tipTargetVariant = computed(() => {
  if (isProductMode.value || isManualItem(props.item)) return '';
  return props.item?.variantName ?? '';
});

const titleKey = computed(() => {
  if (!tipTargetName.value) {
    return isProductMode.value
      ? 'item.suggestSupplier.productSheetTitle'
      : 'item.suggestSupplier.sheetTitle';
  }
  return tipTargetVariant.value
    ? 'item.suggestSupplier.titleForVariant'
    : 'item.suggestSupplier.titleFor';
});

const introKey = computed(() =>
  isProductMode.value ? 'item.suggestSupplier.productIntro' : 'item.suggestSupplier.intro',
);

const suggestionKey = computed(() =>
  isProductMode.value ? 'mySuggestion' : 'supplierSuggestion',
);

// FORGIVING, not a bare substring match (U-79): a member's own spelling of a
// shop routinely disagrees with the roster's — أ/إ/آ, ة/ه, ى/ي, a stray
// tatweel — and a literal `.includes()` turned every one of those into "no
// results" on a roster that in fact had the row. `matchesAllWords` folds the
// spelling and matches word-by-word, any order, so «ريهام جاليري» finds
// «جاليري ريهام».
const filteredSuppliers = computed(() => {
  const query = search.value.trim();
  if (query === '') return props.suppliers;
  return props.suppliers.filter((supplier) => matchesAllWords(query, supplier?.name ?? ''));
});

/**
 * Seed from the suggestion already on record, so re-opening this sheet is an
 * EDIT and not a retype. Runs on open rather than on mount: the host keeps one
 * instance alive across rows, so mount-time seeding would show her the previous
 * line's shop.
 *
 * THE ROUND TRIP (U-31). A shop she originally PICKED used to come back in the
 * free-text field, because the payload reported a display string with no id
 * behind it — and guessing an id back out of a name is the kind of match that
 * silently picks the wrong shop. The backend now derives `supplierId` on read,
 * present ONLY on an exact roster-name match through the same method that
 * pre-selects the leader's accept sheet, so re-selecting the row is safe: the
 * sheet and that sheet can never name different shops for one suggestion.
 *
 * Her typed name is seeded EITHER WAY. It is the fact of record, it is what
 * posts if the row turns out not to be offered, and a shop renamed since she
 * named it correctly reports no id — leaving her words untouched, which is the
 * right way round.
 */
const seededSupplierId = ref(null);

watch(
  () => props.opened,
  (isOpen) => {
    if (!isOpen) return;
    // Seeds from the OPEN tip only. An answered one is history she can read on
    // the surface behind this sheet; re-opening the door means she is telling us
    // something NEW, and pre-filling it with a shop the club already declined
    // would be the sheet arguing with her.
    const existing = openSupplierSuggestion(target.value, suggestionKey.value);
    search.value = '';
    // ANSWER MODE SEEDS FROM HER ANSWER, not from a tip: on a live question
    // there is no `supplierSuggestion` to read — the backend records the shop on
    // the ANSWER and echoes it as `memberSupplierId` — so the host passes that
    // id in and this seeds from it. Editing an answer must re-select the shop
    // she named, exactly as editing a tip does.
    seededSupplierId.value = isAnswerMode.value
      ? (props.seedSupplierId ?? null)
      : (existing?.supplierId ?? null);
    pickedId.value = pickableId(seededSupplierId.value);
    note.value = isAnswerMode.value ? (props.seedNote ?? '') : (existing?.note ?? '');
    // Whole pounds back out of piasters. Integer piasters divided by 100 can
    // carry decimals, and `String()` renders them without trailing zeros —
    // which is what she typed in every real case, since the field steps by 1.
    priceSeen.value =
      !isAnswerMode.value && existing?.priceSeenCents != null
        ? String(existing.priceSeenCents / 100)
        : '';
    touched.value = false;
    submitting.value = false;
    errorMessage.value = null;
  },
);

/**
 * The roster is fetched when the sheet opens, so it usually lands AFTER the
 * seeding above ran against an empty list. Re-apply the seed once it arrives —
 * but only while she has not touched the form, so a list resolving late can
 * never move a choice she has already made.
 */
watch(
  () => props.suppliers,
  () => {
    if (!props.opened || touched.value) return;
    pickedId.value = pickableId(seededSupplierId.value);
  },
);

/** An id is only pre-selectable when the list actually offers that row — the
 *  roster is narrower than the validator's by one rule (a member-typed
 *  organization is never named as a shop), so an id can be real and absent. */
function pickableId(id) {
  if (id == null) return null;
  return props.suppliers.some((supplier) => Number(supplier?.id) === id) ? id : null;
}

function pick(id) {
  touched.value = true;
  pickedId.value = id;
  // A refusal was about the previous shape of the form; keeping it on screen
  // while she changes that shape would read as though the change was rejected.
  errorMessage.value = null;
}

/**
 * The price she typed, in piasters — or null when she typed nothing usable.
 *
 * Three outcomes, and the caller has to tell two of them apart: `null` for "she
 * left it empty" (send no key, which is the common case) and `undefined` for
 * "she typed something that cannot be a price" (refuse before the round-trip).
 * Collapsing those would either drop a real mistake silently or refuse an empty
 * optional field.
 */
function priceSeenCentsOrRefusal() {
  const raw = priceSeen.value;

  // ⚠ IT IS NOT ALWAYS A STRING. Vue's `vModelText` casts on `type="number"`
  // inputs — `castToNumber = number || el.type === 'number'` — so `v-model`
  // writes back a NUMBER once she types a valid one, and the empty field and
  // an unparseable entry both come back as the string `''`. A string-only
  // guard here silently dropped every price she gave.
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' && raw.trim() === '') return null;

  const pounds = Number(toWesternDigits(raw));
  if (!Number.isFinite(pounds)) return undefined;

  // Rounded, not truncated: a half-piastre is not a thing, and `Math.round`
  // keeps «45.5» as 4550 rather than quietly shaving it.
  const cents = Math.round(pounds * 100);
  return cents >= PRICE_SEEN_MIN_CENTS ? cents : undefined;
}

function onClosed() {
  emit('update:opened', false);
}

/**
 * Map a refusal to something she can act on. The backend's own `detail` wins
 * whenever it exists — it is localized and specific ("we don't know that shop",
 * "your note is too long") where ours can only be generic. The three coded arms
 * below are for the refusals whose `detail` is an English exception sentence.
 */
function messageForError(error) {
  // A BARE 403 — one status===403 reached here without a coded reason, or
  // one whose `detail` is Laravel's own English "This action is
  // unauthorized." rather than a member-facing sentence — gets the SAME
  // Arabic sentence a coded 403 already gets. Never the raw English (every
  // tip refusal must show her own language, tips-wave P3-A).
  if (
    error?.code === ORDER_BOX_ITEM_SUGGEST_FORBIDDEN
    || error?.code === PRODUCT_SUGGEST_FORBIDDEN
    || error?.status === 403
  ) {
    return t('item.suggestSupplier.errors.forbidden');
  }
  if (error?.code === ORDER_BOX_ITEM_SUGGEST_ROUND_CLOSED) {
    return t('item.suggestSupplier.errors.roundClosed');
  }
  return error?.detail || t('item.suggestSupplier.errors.generic');
}

async function submit() {
  if (submitting.value) return;

  const targetId = target.value?.id;
  if (targetId == null) return;

  // ONE REQUIRED FIELD, AND IT IS THE WHOLE BODY. `supplier_id` is required on
  // both doors and on the answer path alike, so this is the single refusal the
  // client can rule out before spending a round-trip.
  const supplierId = pickedId.value;
  if (supplierId == null) {
    errorMessage.value = t('item.suggestSupplier.pickRequired');
    // ANNOUNCED, not merely rendered. The submit sits in a pinned footer and is
    // reachable from ANY scroll position — commonly from far above this message
    // with a long roster open — so an inline-only refusal would read as a dead
    // button. Already-localised string; the region below still carries it.
    showError(errorMessage.value);
    return;
  }

  // `undefined` means she typed something that is not a price. Refused here
  // rather than dropped, because a price silently discarded is worse than one
  // she is asked to fix: the leader's accept sheet defaults from it.
  const priceSeenCents = isAnswerMode.value ? null : priceSeenCentsOrRefusal();
  if (priceSeenCents === undefined) {
    errorMessage.value = t('item.suggestSupplier.priceRequired');
    showError(errorMessage.value);
    return;
  }

  // ANSWER MODE POSTS NOTHING FROM HERE. The shop rides her answer through
  // `respond`, which is the host's call to make — it owns the issue, the
  // revision rules and the re-read afterwards. Emitting keeps the 409/422
  // handling for that endpoint in the one place that already does it, instead
  // of a second copy inside a sheet that would then have to know about issue
  // statuses. Closing is the host's too, after its POST resolves.
  if (isAnswerMode.value) {
    emit('pick', { supplierId, note: note.value.trim() });
    return;
  }

  submitting.value = true;
  errorMessage.value = null;

  try {
    // TWO DOORS, ONE BODY SHAPE since 2026-09-15 — but still two functions, and
    // deliberately not one payload object spread into both: neither accepts a
    // key the other refuses any more, and keeping the call sites explicit is
    // what has kept that true through three contract changes.
    const updated = isProductMode.value
      ? await suggestSupplierForProduct(targetId, {
        supplierId,
        note: note.value,
        priceSeenCents,
      })
      : await OrderBoxRepository.suggestSupplier(
        props.item?.orderBoxId ?? null,
        targetId,
        {
          supplierId,
          note: note.value,
          priceSeenCents,
        },
      );
    emit('submitted', updated);
    emit('update:opened', false);
  } catch (error) {
    const message = messageForError(error);

    // A 403 is the ONE refusal that is not about this form. On a line it means
    // the line moved past suggesting — collected, packed or shipped while she
    // typed — or it has spent its one tip; on a product it means the door is
    // not hers to open at all (no membership, or still under review). Either
    // way re-reading the message inside a sheet she can only submit again would
    // be a dead end. Hand it to the host, which closes the sheet, says the
    // sentence as a toast and (on a line) re-reads it so the door stops being
    // offered at all. Every other refusal is about what she entered and stays
    // here, next to the field she can change.
    if (error?.code === ORDER_BOX_ITEM_SUGGEST_FORBIDDEN || error?.code === PRODUCT_SUGGEST_FORBIDDEN) {
      emit('stale', { message, error });
      emit('update:opened', false);
      return;
    }

    errorMessage.value = message;
    // Same reason as the client-side refusal above: the region holding this
    // sentence can sit well outside the visible body now that the CTA is pinned.
    showError(message);
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped>
/*
  DEFINITE height, deliberately — not F7's default and not `auto + max-height`.
  With a definite height Framework7's own chain produces the scroller with no
  flex scaffolding (.sheet-modal-inner 100% -> .page-content 100%; overflow:auto),
  which is why this sheet no longer carries `sheet-scroll`: that utility exists
  only to rescue AUTO-height sheets, and there is no auto height here to rescue.

  85%, not the wallet wizards' 92%: with no roster this body is one sentence,
  and a taller slab would be mostly empty. With a roster it scrolls, which is
  exactly what the definite height buys.

  The unit is a PERCENTAGE of the app root, never vh/dvh: inside Telegram
  `html.telegram-webapp` sizes that root to the visible band while vh measures
  the whole WebView. tests/unit/styles/sheet-scroll-invariant.test.js enforces it.
*/
.supplier-suggestion-sheet {
  height: 85%;

  /* Framework7's .page-content folds this straight into its own padding-bottom
     (framework7-bundle.css:391), so the last element in the body clears the
     sticky footer instead of hiding under it. Keep it in step with the footer's
     height in app.css: that is this var PLUS --f7-safe-area-bottom, and
     .page-content already adds that inset itself. */
  --sheet-fixed-footer-h: 76px;
  --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

/* `.sheet-fixed-footer` is declared in src/css/app.css — shared with every
   sheet on this pattern so they cannot drift. */

.supplier-suggestion-sheet__intro p {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.supplier-suggestion-sheet__reward {
  margin-top: 6px !important;
  color: var(--poolit-text-primary) !important;
  font-weight: var(--poolit-font-weight-medium);
}

.supplier-suggestion-sheet__list {
  margin-top: 0;
}

.supplier-suggestion-sheet__named {
  padding: 2px 8px;
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-primary-50, #eef6f1);
  color: var(--poolit-primary-700, #1f6b45);
  font-size: var(--poolit-font-size-xs);
  white-space: nowrap;
}

.supplier-suggestion-sheet__vote p {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.55;
  color: var(--poolit-text-secondary);
}

.supplier-suggestion-sheet__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.supplier-suggestion-sheet__label {
  font-size: var(--poolit-font-size-sm);
  font-weight: var(--poolit-font-weight-medium);
  color: var(--poolit-text-primary);
}

/* Logical properties throughout, so RTL needs no override block and this file
   never has to reach for `[dir='rtl']` (which a scoped block cannot express). */
.supplier-suggestion-sheet__input,
.supplier-suggestion-sheet__textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--poolit-border, #e2e5e3);
  border-radius: var(--poolit-radius-md);
  background: var(--poolit-surface, #fff);
  color: var(--poolit-text-primary);
  font-family: inherit;
  font-size: var(--poolit-font-size-md);
  line-height: 1.55;
  text-align: start;
}

.supplier-suggestion-sheet__textarea {
  min-height: 62px;
  resize: vertical;
}

.supplier-suggestion-sheet__input:focus-visible,
.supplier-suggestion-sheet__textarea:focus-visible {
  outline: none;
  border-color: var(--poolit-primary-500);
}

.supplier-suggestion-sheet__hint {
  margin: 0;
  font-size: var(--poolit-font-size-xs);
  line-height: 1.5;
  color: var(--poolit-text-muted);
}

.supplier-suggestion-sheet__error p {
  margin: 0;
  font-size: var(--poolit-font-size-sm);
  line-height: 1.5;
  color: var(--poolit-danger, #d1453b);
}
</style>
